import type { FormEvent } from 'react';
import { useCallback } from 'react';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import { useAuthStatus } from '../../allauth_auth';
import FormErrors from '../../components/FormErrors';
import AuthLayout from '../../layouts/AuthLayout';
import { Flows, getPasswordReset } from '../../lib/allauth';
import type { AllauthResponse } from '../../types/allauth';

const CODE_FIELDS = { code: { required: true } } as const;

export default function ConfirmPasswordResetCode() {
  const [, authInfo] = useAuthStatus();
  const [code, setCode] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: AllauthResponse | null;
  }>({ fetching: false, content: null });
  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: CODE_FIELDS,
    values: { code },
  });
  const codeField = getFieldProps('code');

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      touchAll();
      if (!isValid) {
        return;
      }
      setResponse({ ...response, fetching: true });
      getPasswordReset(code)
        .then(content => {
          setResponse(r => {
            return { ...r, content };
          });
        })
        .catch(e => {
          console.error(e);
          toast.error(sanitizeError(e));
        })
        .then(() => {
          setResponse(r => {
            return { ...r, fetching: false };
          });
        });
    },
    [touchAll, isValid, code, response]
  );

  if (
    response.content?.status === 409 ||
    authInfo.pendingFlow?.id !== Flows.PASSWORD_RESET_BY_CODE
  ) {
    return <Navigate to='/account/password/reset' />;
  } else if (response.content?.status === 200) {
    return (
      <Navigate
        state={{ resetKey: code, resetKeyResponse: response.content }}
        to='/account/password/reset/complete'
      />
    );
  }
  return (
    <AuthLayout title='Enter Password Reset Code'>
      <div className='flex flex-col gap-8'>
        {/* Header */}
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Enter password reset code
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            We've sent a verification code to your email. Enter it below to
            reset your password.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} noValidate className='flex flex-col gap-6'>
          <FormErrors errors={response.content?.errors} />

          <div>
            <label
              className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5'
              htmlFor='code'
            >
              Verification code
            </label>
            <Input
              type='text'
              id='code'
              className='w-full text-center text-lg font-mono tracking-widest'
              placeholder='000000'
              isRequired
              onChange={setCode}
              value={code}
              name='code'
              maxLength={6}
              onBlur={codeField.onBlur}
              isInvalid={codeField.showError}
              showError={codeField.showError}
            />
            {codeField.showError && codeField.error && (
              <HintText isInvalid showError className='mt-1.5'>
                {codeField.error}
              </HintText>
            )}
            <FormErrors param='key' errors={response.content?.errors} />
            <p className='mt-1.5 text-xs text-gray-500 dark:text-gray-400 text-center'>
              Code expires shortly, so please enter it soon.
            </p>
          </div>

          <Button
            type='submit'
            isLoading={response.fetching}
            isDisabled={response.fetching}
            showTextWhileLoading={true}
            className='w-full'
          >
            {response.fetching ? 'Confirming…' : 'Confirm'}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
